import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PrinterIcon } from "@heroicons/react/24/outline";
import api, { errorCodes } from "../../api";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";

export const FORBIDDEN_MESSAGE = "No tenés permisos para ver esta sección";

const pages = [
    { name: "Configuración", href: "#", current: false },
    { name: "Templates de impresión", href: "#", current: true },
];

function paperText(template, catalog) {
    if (template.pageSize === "custom")
        return `Personalizado (${Number(template.pageWidthMm)} × ${Number(
            template.pageHeightMm
        )} mm)`;
    const size = catalog.pageSizes.find((s) => s.value === template.pageSize);
    return size ? `${size.label} (${size.widthMm} × ${size.heightMm} mm)` : "";
}

export default function PrintTemplatesPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const [catalog, setCatalog] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.printTemplates.getCatalog(), api.printTemplates.getGlobals()])
            .then(([cat, list]) => {
                setCatalog(cat);
                setTemplates(list.results ?? list);
            })
            .catch((e) =>
                setError(
                    e.code === errorCodes.forbidden
                        ? FORBIDDEN_MESSAGE
                        : "No se pudieron cargar los templates"
                )
            )
            .finally(() => setLoading(false));
    }, []);

    const handleSidebarToggle = () =>
        isDesktop
            ? setSidebarOpenDesktop(!sidebarOpenDesktop)
            : setSidebarOpen(true);

    return (
        <PatientModuleLayout
            currentScreen="Templates de impresión"
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={handleSidebarToggle}
                    mobileBackTo="/profile"
                    mobileBackLabel="Perfil"
                    mobileTitle="Templates de impresión"
                />
            }
        >
            <div className="mx-auto max-w-4xl space-y-3 p-4 lg:p-6">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Templates de impresión
                </h1>
                {loading && (
                    <p className="text-sm text-gray-500">Cargando...</p>
                )}
                {error && (
                    <p className="rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        {error}
                    </p>
                )}
                {catalog &&
                    catalog.documentTypes.map((doc) => {
                        const t = templates.find(
                            (x) => x.documentType === doc.value
                        );
                        if (!t) return null;
                        return (
                            <div
                                key={doc.value}
                                className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center"
                            >
                                <PrinterIcon className="hidden h-6 w-6 shrink-0 text-gray-400 sm:block" />
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-gray-900 dark:text-white">
                                        {doc.label}
                                    </p>
                                    <p className="truncate text-sm text-gray-600 dark:text-gray-300">
                                        {t.title}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {paperText(t, catalog)} · Modificado{" "}
                                        {new Date(t.updatedAt).toLocaleString(
                                            "es-PY"
                                        )}
                                    </p>
                                </div>
                                <Link
                                    to={`/settings/print-templates/${t.id}`}
                                    className="inline-flex justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm"
                                >
                                    Editar
                                </Link>
                            </div>
                        );
                    })}
            </div>
        </PatientModuleLayout>
    );
}
